import React, { useState } from 'react';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const PrivacySecuritySection = ({ 
  formData, 
  handleInputChange, 
  handleCheckboxChange 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const sessionTimeoutOptions = [
    { value: 'never', label: 'Never (stay logged in)' },
    { value: '15', label: '15 minutes' },
    { value: '60', label: '1 hour' },
    { value: '1440', label: '24 hours' },
    { value: 'onclose', label: 'On app close' }
  ];

  const twoFactorMethodOptions = [
    { value: 'sms', label: 'SMS code' },
    { value: 'authenticator', label: 'Authenticator app' },
    { value: 'email', label: 'Email code' }
  ];

  const handleDeleteAccount = () => {
    if (deleteConfirmText === 'DELETE') {
      console.log('Account deletion confirmed');
      setShowDeleteConfirm(false);
      setDeleteConfirmText('');
    }
  };

  const handleExportData = (format) => {
    console.log(`Exporting data as ${format}`);
  };

  const handleClearCache = () => {
    console.log('Clearing cache');
  };

  return (
    <div className="card">
      <div 
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-xl bg-accent-purple/20 glow-subtle">
            <Icon name="Lock" size={20} color="var(--color-accent-purple)" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-foreground">Privacy & Security</h2>
            <p className="text-xs md:text-sm text-muted-foreground font-caption">
              Manage security settings and data privacy
            </p>
          </div>
        </div>
        <Icon 
          name={isExpanded ? "ChevronUp" : "ChevronDown"} 
          size={24} 
          color="var(--color-muted-foreground)" 
        />
      </div>

      {isExpanded && (
        <div className="mt-6 md:mt-8 space-y-6 md:space-y-8">
          <div>
            <h3 className="text-base md:text-lg font-semibold text-foreground mb-4">Authentication</h3>
            <div className="space-y-4">
              <Checkbox
                label="Enable Two-Factor Authentication (2FA)"
                description="Add an extra layer of security to your account"
                checked={formData?.enable2FA}
                onChange={(e) => handleCheckboxChange('enable2FA', e?.target?.checked)}
              />

              {formData?.enable2FA && (
                <Select
                  label="2FA Method"
                  options={twoFactorMethodOptions}
                  value={formData?.twoFactorMethod}
                  onChange={(value) => handleInputChange({ target: { name: 'twoFactorMethod', value } })}
                  placeholder="Select 2FA method"
                  className="w-full"
                />
              )}

              <Checkbox
                label="Enable biometric login (Face ID / Touch ID / Fingerprint)"
                description="Quick unlock with device biometrics"
                checked={formData?.enableBiometric}
                onChange={(e) => handleCheckboxChange('enableBiometric', e?.target?.checked)}
              />
            </div>
          </div>

          <div className="h-px bg-accent-purple/20"></div>

          <div>
            <h3 className="text-base md:text-lg font-semibold text-foreground mb-4">Session Management</h3>
            <Select
              label="Session Timeout"
              options={sessionTimeoutOptions}
              value={formData?.sessionTimeout}
              onChange={(value) => handleInputChange({ target: { name: 'sessionTimeout', value } })}
              placeholder="Select timeout duration"
              description="Auto-logout after inactivity"
              className="w-full"
            />
          </div>

          <div className="h-px bg-accent-purple/20"></div>

          <div>
            <h3 className="text-base md:text-lg font-semibold text-foreground mb-4">Data Management</h3>
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-tertiary border border-accent-cyan/20">
                <div className="flex items-center gap-3 mb-3">
                  <Icon name="Download" size={20} color="var(--color-accent-cyan)" />
                  <div>
                    <h4 className="text-sm font-semibold text-foreground">Export My Data</h4>
                    <p className="text-xs text-muted-foreground">Download all your data for backup or portability</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleExportData('json')}
                  >
                    Export as JSON
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleExportData('csv')}
                  >
                    Export as CSV
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleExportData('pdf')}
                  >
                    Export as PDF
                  </Button>
                </div>
              </div>

              <div className="p-4 rounded-lg bg-tertiary border border-accent-blue/20">
                <div className="flex items-center gap-3 mb-3">
                  <Icon name="Trash2" size={20} color="var(--color-accent-blue)" />
                  <div>
                    <h4 className="text-sm font-semibold text-foreground">Clear Cache</h4>
                    <p className="text-xs text-muted-foreground">Free up space and force fresh data reload</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearCache}
                >
                  Clear Local Cache
                </Button>
              </div>

              <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/20">
                <div className="flex items-center gap-3 mb-3">
                  <Icon name="AlertTriangle" size={20} color="var(--color-destructive)" />
                  <div>
                    <h4 className="text-sm font-semibold text-destructive">Delete Account</h4>
                    <p className="text-xs text-muted-foreground">Permanently delete your account and all data</p>
                  </div>
                </div>
                {!showDeleteConfirm ? (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    Delete My Account
                  </Button>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-destructive font-medium">Type DELETE to confirm:</p>
                    <input
                      type="text"
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e?.target?.value)}
                      className="w-full px-3 py-2 rounded-md border border-destructive bg-background text-foreground text-sm"
                      placeholder="DELETE"
                    />
                    <div className="flex gap-2">
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={handleDeleteAccount}
                        disabled={deleteConfirmText !== 'DELETE'}
                      >
                        Confirm Delete
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setShowDeleteConfirm(false);
                          setDeleteConfirmText('');
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="h-px bg-accent-purple/20"></div>

          <div>
            <h3 className="text-base md:text-lg font-semibold text-foreground mb-4">Audit Log</h3>
            <Button
              variant="outline"
              iconName="FileText"
              iconPosition="left"
            >
              View Account Activity
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrivacySecuritySection;